import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, Cloud, CloudOff, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SyncIndicator = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAgo, setLastSyncAgo] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await axios.get(`${API}/ecosystem/sync/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSyncStatus(response.data);
    } catch (err) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Update "ago" text every 10s
  useEffect(() => {
    const updateAgo = () => {
      if (!syncStatus?.last_sync) {
        setLastSyncAgo("Never");
        return;
      }
      const now = new Date();
      const last = new Date(syncStatus.last_sync);
      const diffSec = Math.floor((now - last) / 1000);
      if (diffSec < 10) setLastSyncAgo("Just now");
      else if (diffSec < 60) setLastSyncAgo(`${diffSec}s ago`);
      else if (diffSec < 3600) setLastSyncAgo(`${Math.floor(diffSec / 60)}m ago`);
      else if (diffSec < 86400) setLastSyncAgo(`${Math.floor(diffSec / 3600)}h ago`);
      else setLastSyncAgo(`${Math.floor(diffSec / 86400)}d ago`);
    };
    updateAgo();
    const interval = setInterval(updateAgo, 10000);
    return () => clearInterval(interval);
  }, [syncStatus]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      // Push current local state
      await axios.post(`${API}/ecosystem/sync/push`, {
        source_app: "web",
        timestamp: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Re-fetch status
      await fetchStatus();
      toast.success("Sync complete!");
    } catch (err) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const connected = syncStatus?.sync_enabled !== false;
  const appCount = syncStatus?.connected_apps?.length || 1;

  return (
    <div
      data-testid="sync-indicator"
      className="flex items-center gap-2 px-3 py-2 rounded-sm bg-zinc-900/50 border border-zinc-800 group"
    >
      {/* Status icon */}
      <div className="relative">
        {connected ? (
          <Cloud className="w-3.5 h-3.5 text-emerald-500" />
        ) : (
          <CloudOff className="w-3.5 h-3.5 text-red-500" />
        )}
        {/* Pulse dot when recently synced */}
        {lastSyncAgo === "Just now" && (
          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping" />
        )}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-wider leading-tight">
          Synced {lastSyncAgo}
        </p>
        <p className="text-[8px] text-zinc-600 truncate">
          {appCount} app{appCount > 1 ? "s" : ""} connected
        </p>
      </div>

      {/* Manual sync button */}
      <button
        data-testid="manual-sync-btn"
        onClick={triggerSync}
        disabled={syncing}
        className="p-1 rounded-sm text-zinc-500 hover:text-emerald-400 hover:bg-zinc-800 transition-colors disabled:opacity-50"
        title="Sync now"
      >
        {syncing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <RefreshCw className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
};

export default SyncIndicator;
